'use client';

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { DataTable } from '@/components/ui/data-table';
import { Panel } from '@/components/ui/panel';
import { getProfileHref } from '@/lib/profile/profile-href';
import { useUserProfilePrefetchIntent } from '@/lib/sdk/prefetch';
import { cn } from '@/lib/ui/utils';
import type { MarketTradersSort, MarketTraderViewModel } from './trader-row';
import { TraderRowCells } from './trader-row-cells';
import { MARKET_TRADERS_MOBILE_ROW_CLASS } from './trader-row-layout';
const NOOP_SORT = (): void => {};
interface MarketYourRankCardProps {
  /** The viewer's resolved traders row. The container decides visibility. */
  viewModel: MarketTraderViewModel;
  /** The viewer's userId — drives the profile link + prefetch. */
  userId: string;
  sortBy: MarketTradersSort;
  sortOrder?: 'asc' | 'desc';
  visible: boolean;
}
const VISIBLE = {
  opacity: 1,
  y: 0
} as const;
const ENTER_OFFSET = {
  opacity: 0,
  y: 16
} as const;
const TRANSITION = {
  duration: 0.2,
  ease: [0.16, 1, 0.3, 1]
} as const;

/**
 * Floating "Your rank" bar — pinned to the bottom of the Traders panel
 * (leaderboard-style), reusing the table's own DataTable cells so the bar and
 * the rows above it can never drift. No title; the inline "You" badge marks it.
 *
 * Presentational only — the container owns the data + visibility (it gates both
 * this bar and the list's bottom padding on the same condition, so the bar never
 * covers the last row and there's no dead space when it's hidden).
 *
 * `inheritBackground` drops Panel's solid `bg-background-primary` so the
 * `bg-background-secondary/70` tint + blur read as translucent; `[&_tr]` / `[&_a]`
 * zero out the DataTable row's own solid fill (desktop `<tr>` / mobile `<a>`) so
 * only the pill shows. No `PanelContent` — the row's `pl-4/pr-4` supplies the
 * single horizontal padding (no stacked `px-4`).
 */
export function MarketYourRankCard(t0) {
  const $ = _c(11);
  const {
    viewModel,
    userId,
    sortBy,
    sortOrder: t1,
    visible
  } = t0;
  const sortOrder = t1 === undefined ? "desc" : t1;
  const t = useTranslations("market.traders");
  const intent = useUserProfilePrefetchIntent(userId.length > 0 ? userId : null);
  const reduceMotion = useReducedMotion() === true;
  let t2;
  if ($[0] !== intent || $[1] !== reduceMotion || $[2] !== sortBy || $[3] !== sortOrder || $[4] !== t || $[5] !== userId || $[6] !== viewModel || $[7] !== visible) {
    t2 = visible ? <m.div initial={reduceMotion ? false : ENTER_OFFSET} animate={VISIBLE} exit={reduceMotion ? VISIBLE : ENTER_OFFSET} transition={reduceMotion ? {
      duration: 0
    } : TRANSITION} className="pointer-events-none absolute inset-x-0 bottom-0 z-overlay"><Panel inheritBackground={true} tone="accent" density="compact" className="pointer-events-auto overflow-hidden rounded-control border border-border-primary bg-background-secondary/70 py-0! shadow-elevation-sticky supports-backdrop-filter:backdrop-blur-xl [&_a]:bg-transparent [&_tr]:bg-transparent"><DataTable entries={[viewModel]} sortBy={sortBy} sortOrder={sortOrder} onSortChange={NOOP_SORT} onSortOrderChange={NOOP_SORT} panel="borderless" cardUntil="xl"><DataTable.Body>{entry => <DataTable.Row key={userId.length > 0 ? userId : "your-rank"} href={getProfileHref(userId)} intent={intent} ariaLabel={t("aria_view_profile")} data-testid="market-your-rank-row" className={cn(MARKET_TRADERS_MOBILE_ROW_CLASS, "hover:bg-transparent! hover:shadow-none! [&>td]:bg-transparent!")}><TraderRowCells entry={entry} isUser={true} /></DataTable.Row>}</DataTable.Body></DataTable></Panel></m.div> : null;
    $[0] = intent;
    $[1] = reduceMotion;
    $[2] = sortBy;
    $[3] = sortOrder;
    $[4] = t;
    $[5] = userId;
    $[6] = viewModel;
    $[7] = visible;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t2) {
    t3 = <LazyMotion features={domAnimation}><AnimatePresence initial={false}>{t2}</AnimatePresence></LazyMotion>;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}